import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useDomains } from "../lib/domain-context";
import { Plus, Trash2 } from "lucide-react";

interface Grant {
  notifyDomainId: string;
  domainName: string;
  senderDomain: string;
  senderLocalPart: string;
}

interface User {
  id: string; email: string; name: string; role: string;
  senderLocalPart: string; suspendedAt: string | null;
  lastLoginAt: string | null; createdAt: string;
  grants: Grant[];
}

interface MeForRole {
  user: { id: string; role: string };
}

export default function UsersPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api<{ users: User[] }>("/api/users") });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api<MeForRole>("/api/auth/me") });
  const [showInvite, setShowInvite] = useState(false);
  const isSuperAdmin = me?.user.role === "super_admin";

  const suspend = useMutation({
    mutationFn: (b: { userId: string; suspended: boolean }) => api("/api/users/suspend", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/users/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
        <button onClick={() => setShowInvite(true)}
          className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-indigo-700">
          <Plus size={14} /> Invite user
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">Adding a user requires OTP confirmation by the gatekeeper.</p>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Sender(s)</th>
              <th className="text-left px-4 py-2">Last login</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users ?? []).map((u) => (
              <tr key={u.id} className="border-t border-gray-100 align-top">
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{u.role}</span></td>
                <td className="px-4 py-3 font-mono text-xs">
                  {u.grants.length === 0 ? (
                    <span className="text-amber-600">No domains granted</span>
                  ) : (
                    <div className="space-y-0.5">
                      {u.grants.map((g) => (
                        <div key={g.notifyDomainId}>{g.senderLocalPart}@{g.senderDomain}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : "never"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.suspendedAt
                    ? <button onClick={() => suspend.mutate({ userId: u.id, suspended: false })} className="text-xs text-indigo-600 hover:underline mr-3">Unsuspend</button>
                    : <button onClick={() => { if (confirm("Suspend this user?")) suspend.mutate({ userId: u.id, suspended: true }); }} className="text-xs text-red-600 hover:underline mr-3">Suspend</button>}
                  {isSuperAdmin && u.id !== me?.user.id && (
                    <button onClick={() => {
                        if (confirm(`Permanently delete ${u.email}? This removes their account, all contacts, lists, templates and campaigns. Cannot be undone.`)) {
                          remove.mutate(u.id);
                        }
                      }}
                      title="Delete permanently"
                      className="text-gray-400 hover:text-red-600 inline-flex items-center gap-1 text-xs">
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteModal onClose={() => { setShowInvite(false); qc.invalidateQueries({ queryKey: ["users"] }); }} />}
    </div>
  );
}

interface AdminDomain {
  id: string;
  domainName: string;
  senderDomain: string;
  suspendedAt: string | null;
}

function InviteModal({ onClose }: { onClose: () => void }) {
  // Domain list visible to the inviter — fetched from the admin
  // notify-domains endpoint (tenant-scoped). Different from useDomains()
  // which only knows the inviter's OWN grants; for inviting we want to
  // see every domain in the tenant.
  const { data: domainsData } = useQuery({
    queryKey: ["admin-notify-domains"],
    queryFn: () => api<{ rows: AdminDomain[] }>("/api/admin/notify-domains"),
  });
  // Fallback for non-admin / no-access cases — use the inviter's own grants
  const fallback = useDomains();
  const tenantDomains: AdminDomain[] = domainsData?.rows
    ?? fallback.domains.map((d) => ({ id: d.notifyDomainId, domainName: d.domainName, senderDomain: d.senderDomain, suspendedAt: null }));
  const activeDomains = tenantDomains.filter((d) => !d.suspendedAt);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  // grants[notifyDomainId] = senderLocalPart (empty = not granted)
  const [grants, setGrants] = useState<Record<string, string>>({});
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState("");

  function toggleDomain(id: string, defaultLocal: string) {
    setGrants((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = defaultLocal;
      return next;
    });
  }
  function setLocal(id: string, local: string) {
    setGrants((prev) => ({ ...prev, [id]: local }));
  }
  // Auto-suggest local-part from email — `user@wenvia.global` → `user`
  const suggestedLocal = email.split("@")[0]?.toLowerCase().replace(/[^a-z0-9._-]/g, "") || "";

  const grantList = Object.entries(grants)
    .filter(([, local]) => local.trim().length >= 2)
    .map(([notifyDomainId, senderLocalPart]) => ({ notifyDomainId, senderLocalPart: senderLocalPart.trim() }));

  const request = useMutation({
    mutationFn: () => api<{ otpId: string }>("/api/users/request-invite", {
      method: "POST",
      body: JSON.stringify({ email, name, role, grants: grantList }),
    }),
    onSuccess: (r) => setOtpId(r.otpId),
  });
  const confirm = useMutation({
    mutationFn: () => api("/api/users/confirm-invite", { method: "POST", body: JSON.stringify({ otpId, code }) }),
    onSuccess: onClose,
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 mb-4">Invite user</h3>

        {!otpId ? (
          <>
            <label className="block text-xs font-medium text-gray-600 mb-1">Wenmail address (their login)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@wenvia.global"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
            <label className="block text-xs font-medium text-gray-600 mb-1">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />

            <label className="block text-xs font-medium text-gray-600 mb-1">Domains &amp; senders</label>
            <p className="text-xs text-gray-500 mb-2">
              Tick each domain this user can send from. The local-part forms their sender
              address on that domain — must be 2-32 chars, unique per domain.
            </p>
            <div className="border border-gray-200 rounded mb-3 max-h-48 overflow-y-auto">
              {activeDomains.length === 0 && (
                <p className="text-xs text-amber-700 px-3 py-2">
                  No Notify domains configured. Add one in Domains first.
                </p>
              )}
              {activeDomains.map((d) => {
                const checked = d.id in grants;
                return (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 last:border-0 cursor-pointer">
                    <input type="checkbox" checked={checked}
                      onChange={() => toggleDomain(d.id, suggestedLocal)} />
                    <span className="text-xs text-gray-700 flex-1">{d.senderDomain}</span>
                    {checked && (
                      <input
                        value={grants[d.id]}
                        onChange={(e) => setLocal(d.id, e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
                        placeholder="local-part"
                        className="border border-gray-300 rounded px-2 py-0.5 text-xs font-mono w-28"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </label>
                );
              })}
            </div>

            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as "admin" | "user")}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
            {request.error && <p className="text-sm text-red-600 mb-2">{(request.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
              <button onClick={() => request.mutate()}
                disabled={!email || !name || grantList.length === 0 || request.isPending}
                className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
                {request.isPending ? "Sending OTP…" : "Send invite OTP"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-3">A code has been sent to the gatekeeper. Enter it to create the user. The new user will receive their temp password via wenmail.</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" autoFocus
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono mb-3" />
            {confirm.error && <p className="text-sm text-red-600 mb-2">{(confirm.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
              <button onClick={() => confirm.mutate()} disabled={code.length < 4 || confirm.isPending}
                className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
                {confirm.isPending ? "Creating…" : "Create user"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
